import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import Enumerable from 'linq';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import TemplateMessageItem from './TemplateMessageItem';

import CancelButton from '@/components/atoms/CancelButton';
import { LineClampTypography } from '@/components/atoms/LineClampTypography';
import { useKeyupListener } from '@/components/hooks/useKeyupListener';
import useConfirmContinueDialog from '@/components/organisms/confirmDialog/useConfirmContinueDialog';

import { ChatRoomsTemplateMessage } from '@/types/graphql';

type ChatFooterTemplateProps = {
  messages: ChatRoomsTemplateMessage[];
  language: string;
  isRemoteMode: boolean;
  onSend: (id: string) => void;
  onCancel: () => void;
};

const ChatFooterTemplate = (props: ChatFooterTemplateProps) => {
  const { t } = useTranslation('chat');
  const boxShadow = '0px 0px 6px rgba(0, 0, 0, 0.2)';

  // メッセージをグループ化し、さらにカテゴリでグループ化する。
  // メッセージ、グループ、カテゴリはここですべて正しくまとめる。
  const categories = useMemo(
    () =>
      Enumerable.from(props.messages)
        .groupBy((x) => x.templateMessage.category.id)
        .orderBy((x) => x.first().templateMessage.category.order ?? 0)
        .select((x) => {
          const msgs = x.toArray();
          // グループがあるものとないものに分解し、あとから結合する。
          const noGroupedMsgs = msgs
            .filter((x) => x.templateMessage.groupId == null)
            .map((x) => ({
              isGroup: false,
              order: x.templateMessage.order,
              items: [x],
            }));
          const groupedMsgs = Enumerable.from(msgs)
            .where((x) => x.templateMessage.groupId != null)
            .groupBy((x) => x.templateMessage.groupId ?? '')
            .select((x) => ({
              isGroup: true,
              order: x.first().templateMessage.order ?? 0,
              items: x.orderBy((y) => y.templateMessage.order).toArray(),
            }))
            .toArray();
          const groups = Enumerable.from(noGroupedMsgs)
            .concat(groupedMsgs)
            .orderBy((x) => x.order)
            .thenBy((x) => (x.isGroup ? 0 : 1))
            .toArray();
          return {
            id: x.key(),
            name: x.toArray()[0].templateMessage.category.name,
            groups,
          };
        })
        .toArray(),
    [props.messages]
  );

  // // テンプレート表示時、最後に送信したメッセージの次のものを表示する。
  // // ※ この仕様は必要なくなり無効化
  // const [initialTemplate, setInitialTemplate] = useState(() => {
  //   // ソート済みのカテゴリーのグループの中から、1つ目のテンプレートのみを抽出したリスト作成。
  //   const allGroupFirstTemplates = Enumerable.from(categories)
  //     .selectMany((x) => x.groups)
  //     .select((x) => x.items[0])
  //     .toArray();

  //   // 最後に送信したテンプレートを取得
  //   const lastTemplate = Enumerable.from(allGroupFirstTemplates)
  //     .where((x) => x.lastSentTime != null)
  //     .orderByDescending((x) => x.lastSentTime)
  //     .firstOrDefault()?.templateMessage;

  //   const lastSendedIndex =
  //     allGroupFirstTemplates.findIndex(
  //       (x) => x.templateMessage.id === lastTemplate?.id
  //     ) ?? -1;

  //   // 最後のものが再生されていたらいったん無効化する
  //   if (lastSendedIndex >= allGroupFirstTemplates.length - 1) {
  //     return undefined;
  //   } else {
  //     return allGroupFirstTemplates[lastSendedIndex + 1].templateMessage;
  //   }
  // });

  const { onSend, isRemoteMode } = props;

  const [focusedTemplateId, setFocusedTemplateId] = useState<
    string | undefined
  >(() => categories[0]?.groups[0]?.items[0]?.templateMessage.id);

  // use to handler user spam arrow left
  const lastSentAt = useRef<number>(0);
  const handleSendAndMoveToNext = useCallback(
    (templateId: string) => {
      const now = Date.now();

      if (now - lastSentAt.current < 500) return;
      if (!isRemoteMode) return;

      // 現在選択されている項目を送信
      if (templateId) {
        onSend(templateId);

        // 続くテンプレートに選択を切り替え
        {
          // ソート済みのカテゴリーのグループの中から、1つ目のテンプレートのみを抽出したリスト作成。
          const allGroupFirstTemplates = Enumerable.from(categories)
            .selectMany((x) => x.groups)
            .select((x) => x.items[0])
            .toArray();

          const lastSendedIndex =
            allGroupFirstTemplates.findIndex(
              (x) => x.templateMessage.id === templateId
            ) ?? -1;

          const nextTemplate =
            lastSendedIndex + 1 < allGroupFirstTemplates.length
              ? allGroupFirstTemplates[lastSendedIndex + 1]
              : allGroupFirstTemplates[0];

          // 次のものを選択
          setFocusedTemplateId(nextTemplate?.templateMessage.id);

          lastSentAt.current = now;
        }
      }
    },
    [categories, onSend, isRemoteMode]
  );

  useKeyupListener(
    useMemo(() => ['ArrowLeft'], []),
    useCallback(() => {
      if (focusedTemplateId) {
        handleSendAndMoveToNext(focusedTemplateId);
      }
    }, [focusedTemplateId, handleSendAndMoveToNext])
  );

  //use to handle scroll when remote mode enable
  const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  useEffect(() => {
    const target = buttonRefs.current[focusedTemplateId ?? ''];
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [focusedTemplateId]);

  const resendConfirmDialog = useConfirmContinueDialog(true);

  return (
    <Box
      width={1}
      height="300px"
      boxShadow={boxShadow}
      bgcolor={'white'}
      display="grid"
      gridTemplateRows="auto auto auto 1fr"
      data-testid="chat-footer-template"
    >
      {!isRemoteMode && (
        <>
          <Box
            mt={1}
            width={1}
            display="grid"
            gridTemplateColumns="1fr auto 1fr"
            alignItems="center"
          >
            <CancelButton
              onClick={props.onCancel}
              variant="text"
              sx={{ mr: 'auto' }}
            />
            <Typography variant="h6">{t('templateSelect.title')}</Typography>
          </Box>
          <Divider />
        </>
      )}
      <Box width={1} height={1} pb={1} overflow="auto" id="template-scroll">
        <List sx={{ p: 0 }} key="folders">
          {isRemoteMode
            ? categories.map((category) => (
                <Box key={category.id} mx={2}>
                  <Typography
                    variant="subtitle1"
                    sx={{ color: (t) => t.palette.text.secondary }}
                    my={2}
                  >
                    {category.name}
                  </Typography>
                  <Stack spacing={1}>
                    {category.groups.map((group, groupIndex) => (
                      <TemplateMessageItem
                        key={groupIndex}
                        isRemoteMode={isRemoteMode}
                        templates={group.items}
                        language={props.language}
                        templateMessageId={focusedTemplateId}
                        onRemoteClickSend={handleSendAndMoveToNext}
                        onSend={props.onSend}
                        onRef={(element) => {
                          const id = group.items[0].templateMessage.id;
                          if (id == null) return;
                          buttonRefs.current[id] = element;
                          //   const parent =
                          //     document.getElementById('template-scroll');
                          //   if (element && parent && id === initialTemplate?.id) {
                          //     const targetTop =
                          //       element.getBoundingClientRect().top;
                          //     const parentTop =
                          //       parent.getBoundingClientRect().top;
                          //     const scrollY = targetTop - parentTop - 8;
                          //     parent.scrollTo({ top: scrollY });
                          //     setInitialTemplate(undefined);
                          //   }
                        }}
                        resendConfirmDialog={resendConfirmDialog}
                      />
                    ))}
                  </Stack>
                </Box>
              ))
            : categories.map((category) => (
                <Accordion key={category.id}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                  >
                    <ListItem
                      secondaryAction={
                        <Typography color={(t) => t.palette.grey[600]}>
                          {category.groups.length + t('templateSelect.unit')}
                        </Typography>
                      }
                      disablePadding
                    >
                      <ListItemIcon sx={{ minWidth: 'auto', mr: 1 }}>
                        <FolderIcon
                          sx={{ fontSize: '1.8rem' }}
                          htmlColor="#9dc5f5"
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <LineClampTypography variant="body1" rowCount={2}>
                            {category.name}
                          </LineClampTypography>
                        }
                      />
                    </ListItem>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={1}>
                      {category.groups.map((group, groupIndex) => (
                        <TemplateMessageItem
                          key={groupIndex}
                          templates={group.items}
                          isRemoteMode={isRemoteMode}
                          language={props.language}
                          templateMessageId={focusedTemplateId}
                          onRemoteClickSend={handleSendAndMoveToNext}
                          onSend={props.onSend}
                          // onRef={(element) => {
                          //   const id = group.items[0].templateMessage.id;
                          //   if (id == null) return;

                          //   buttonRefs.current[id] = element;
                          //   const parent =
                          //     document.getElementById('template-scroll');
                          //   if (
                          //     element &&
                          //     parent &&
                          //     id === initialTemplate?.id
                          //   ) {
                          //     const targetTop =
                          //       element.getBoundingClientRect().top;
                          //     const parentTop =
                          //       parent.getBoundingClientRect().top;
                          //     const scrollY = targetTop - parentTop - 8;
                          //     parent.scrollTo({ top: scrollY });
                          //     setInitialTemplate(undefined);
                          //   }
                          // }}
                          resendConfirmDialog={resendConfirmDialog}
                        />
                      ))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))}
        </List>
      </Box>
    </Box>
  );
};
export default ChatFooterTemplate;
